import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Check, Loader2, Gift, Clock, Star, Shield, Zap, Users, Brain, Heart, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import PublicHeader from '../components/PublicHeader';
import { AdminEditButton } from '../components/admin/AdminEditButton';
import { useFoundingOffer } from '../components/FoundingMember';
import { toast } from 'sonner';
import { CurrencySelector, getStoredCurrency, formatPrice, convertPrice, getCurrencyInfo } from '../components/CurrencySelector';

const API = process.env.REACT_APP_BACKEND_URL;

const Paywall = () => {
    const navigate = useNavigate();
    const { token, isAuthenticated } = useAuth();
    const [billingCycle, setBillingCycle] = useState('yearly');
    const [loadingPackage, setLoadingPackage] = useState(null);
    const [pageContent, setPageContent] = useState(null);
    const [tiers, setTiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAllFeatures, setShowAllFeatures] = useState(false);
    const [currency, setCurrency] = useState(getStoredCurrency());
    
    const { offer: foundingOffer, prices: foundingPrices, isActive: isFoundingActive } = useFoundingOffer();

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const pageResponse = await fetch(`${API}/api/pages/pricing`);
                if (pageResponse.ok) {
                    const data = await pageResponse.json();
                    setPageContent(data);
                }
                
                const tiersResponse = await fetch(`${API}/api/subscription-tiers`);
                if (tiersResponse.ok) {
                    const tiersData = await tiersResponse.json();
                    // Merge API data with default tier enhancements
                    const apiTiers = tiersData.tiers || [];
                    const defaults = getDefaultTiers();
                    const mergedTiers = apiTiers.map(apiTier => {
                        const defaultTier = defaults.find(d => d.id === apiTier.id);
                        return {
                            ...apiTier,
                            highlights: defaultTier?.highlights || apiTier.highlights || [],
                            icon: defaultTier?.icon || Heart,
                            cta: defaultTier?.cta || apiTier.cta || 'Choose Plan'
                        };
                    });
                    setTiers(mergedTiers.length > 0 ? mergedTiers : defaults);
                } else {
                    setTiers(getDefaultTiers());
                }
            } catch (error) {
                console.error('Error fetching content:', error);
                setTiers(getDefaultTiers());
            } finally {
                setLoading(false);
            }
        };
        fetchContent();
    }, []);

    const getDefaultTiers = () => [
        {
            id: 'free',
            name: 'Free',
            subtitle: 'Explore',
            description: 'Get started with core regulation tools',
            monthlyPrice: 0,
            yearlyPrice: 0,
            packageId: null,
            highlights: ['Initial assessment', '3 breathing patterns', 'Educational blog access'],
            cta: 'Start Free',
            icon: Heart
        },
        {
            id: 'essentials',
            name: 'Essentials',
            subtitle: 'Build habits',
            description: 'Full grounding toolkit for daily practice',
            monthlyPrice: 14.99,
            yearlyPrice: 119,
            packageId: 'essentials',
            highlights: ['All breathing patterns (10+)', 'Micro-reset tool', 'Body scan meditation', 'Progress tracking', 'Save favourites'],
            cta: 'Get Essentials',
            icon: Zap
        },
        {
            id: 'support_plus',
            name: 'Support+',
            subtitle: 'Most popular',
            description: 'AI-powered guidance & personalized support',
            monthlyPrice: 29.99,
            yearlyPrice: 229,
            packageId: 'support_plus',
            highlights: ['Everything in Essentials', 'AI Guide companion', 'Guided meditations', 'Self-talk reframes', 'Pause & Repair scripts'],
            cta: 'Get Support+',
            highlighted: true,
            icon: Brain
        },
        {
            id: 'partner',
            name: 'Partner',
            subtitle: 'For couples',
            description: 'Co-regulate with someone you trust',
            monthlyPrice: 49.99,
            yearlyPrice: 379,
            packageId: 'partner',
            highlights: ['Everything in Support+', 'Partner co-regulation mode', 'Shared sessions', 'Communication scripts', 'Relationship templates'],
            cta: 'Get Partner',
            icon: Users
        }
    ];

    const getDiscountedPrice = (tier, billing) => {
        if (!isFoundingActive || !foundingPrices?.prices) return null;
        const packageId = `${tier.id}_${billing}`;
        const priceData = foundingPrices.prices[packageId];
        if (priceData?.has_discount) {
            return priceData.discounted;
        }
        return null;
    };

    const getPrice = (tier) => {
        if (tier.monthlyPrice === 0) return 0;
        const basePrice = billingCycle === 'monthly' ? tier.monthlyPrice : tier.yearlyPrice;
        const discounted = getDiscountedPrice(tier, billingCycle);
        return discounted !== null ? discounted : basePrice;
    };

    const getOriginalPrice = (tier) => {
        if (tier.monthlyPrice === 0) return null;
        const discounted = getDiscountedPrice(tier, billingCycle);
        if (discounted !== null) {
            return billingCycle === 'monthly' ? tier.monthlyPrice : tier.yearlyPrice;
        }
        return null;
    };

    const getMonthlyEquivalent = (tier) => {
        if (tier.monthlyPrice === 0 || billingCycle === 'monthly') return null;
        const yearlyPrice = getPrice(tier);
        return (yearlyPrice / 12).toFixed(2);
    };

    const getSavingsPercent = (tier) => {
        if (tier.monthlyPrice === 0 || billingCycle === 'monthly') return null;
        const monthlyTotal = tier.monthlyPrice * 12;
        const yearlyPrice = getPrice(tier);
        const savings = Math.round(((monthlyTotal - yearlyPrice) / monthlyTotal) * 100);
        return savings > 0 ? savings : null;
    };

    const handleCheckout = async (tierId) => {
        if (!isAuthenticated) {
            toast.info('Please create an account first');
            navigate('/auth', { state: { returnTo: '/pricing' } });
            return;
        }

        if (tierId === 'free') {
            navigate('/home');
            return;
        }

        const packageId = billingCycle === 'monthly' 
            ? `${tierId}_monthly` 
            : `${tierId}_yearly`;

        // Navigate to embedded checkout page with currency
        navigate(`/checkout?package=${packageId}&currency=${currency}`);
    };

    // Feature comparison data - organized to show key differentiators clearly
    const featureCategories = [
        {
            name: 'Key Differentiators',
            features: [
                { name: 'AI Guide companion', free: false, essentials: false, support: true, partner: true },
                { name: 'Partner co-regulation mode', free: false, essentials: false, support: false, partner: true },
                { name: 'Guided meditations library', free: false, essentials: false, support: true, partner: true },
                { name: 'Shared grounding sessions', free: false, essentials: false, support: false, partner: true },
                { name: 'Communication scripts', free: false, essentials: false, support: false, partner: true },
            ]
        },
        {
            name: 'Regulation Tools',
            features: [
                { name: 'Basic breathing patterns (3)', free: true, essentials: true, support: true, partner: true },
                { name: 'All breathing patterns (10+)', free: false, essentials: true, support: true, partner: true },
                { name: 'Micro-reset quick tool', free: false, essentials: true, support: true, partner: true },
                { name: 'Body scan meditation', free: false, essentials: true, support: true, partner: true },
                { name: 'Self-talk reframe tools', free: false, essentials: false, support: true, partner: true },
                { name: 'Pause + Repair scripts', free: false, essentials: false, support: true, partner: true },
            ]
        },
        {
            name: 'Core Features',
            features: [
                { name: 'Initial emotional assessment', free: true, essentials: true, support: true, partner: true },
                { name: 'Educational blog & articles', free: true, essentials: true, support: true, partner: true },
                { name: 'Save favourite tools', free: false, essentials: true, support: true, partner: true },
                { name: 'Progress tracking', free: false, essentials: true, support: true, partner: true },
                { name: 'Relationship templates', free: false, essentials: false, support: false, partner: true },
                { name: 'Priority new features', free: false, essentials: false, support: false, partner: true },
            ]
        }
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FDF8F4] flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-[#1D6B6B] mx-auto mb-4" />
                    <p className="text-[#7D8E95]">Loading plans...</p>
                </div>
            </div>
        );
    }

    const displayTiers = tiers.length > 0 ? tiers : getDefaultTiers();

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FDF8F4]">
            <Helmet>
                <title>Pricing | CoRegulate - ADHD Emotional Regulation</title>
                <meta name="description" content="Choose the right CoRegulate plan for your ADHD journey. From free tools to comprehensive support." />
                <link rel="canonical" href="https://coregulateadhd.com/pricing" />
            </Helmet>
            
            <AdminEditButton tab="pages" />
            
            {/* Header with mobile menu */}
            <PublicHeader 
                currentPage="pricing"
                rightContent={
                    <CurrencySelector 
                        value={currency} 
                        onChange={setCurrency} 
                        variant="compact"
                    />
                }
            />

            {/* Spacer for fixed header */}
            <div className="h-16"></div>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
                
                {/* Hero Section */}
                <div className="text-center mb-10 sm:mb-14">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1D6B6B]/10 text-[#1D6B6B] text-sm font-medium mb-6">
                        <Shield className="w-4 h-4" />
                        Cancel anytime. No lock-in.
                    </div>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1D6B6B] mb-4">
                        Choose your path to calm
                    </h1>
                    <p className="text-lg sm:text-xl text-[#344648] max-w-2xl mx-auto mb-8">
                        Start free, upgrade when you're ready. Your nervous system comes first.
                    </p>

                    {/* Billing Toggle */}
                    <div className="inline-flex items-center p-1.5 rounded-full bg-white shadow-lg border border-[#E8E1D6]">
                        <button
                            onClick={() => setBillingCycle('monthly')}
                            className={`px-5 sm:px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                                billingCycle === 'monthly'
                                    ? 'bg-[#1D6B6B] text-white shadow-md'
                                    : 'text-[#5A7A7A] hover:text-[#1D6B6B]'
                            }`}
                            data-testid="monthly-toggle"
                        >
                            Monthly
                        </button>
                        <button
                            onClick={() => setBillingCycle('yearly')}
                            className={`px-5 sm:px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                                billingCycle === 'yearly'
                                    ? 'bg-[#1D6B6B] text-white shadow-md'
                                    : 'text-[#5A7A7A] hover:text-[#1D6B6B]'
                            }`}
                            data-testid="yearly-toggle"
                        >
                            Yearly
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                                billingCycle === 'yearly' 
                                    ? 'bg-white/20 text-white' 
                                    : 'bg-[#E08850]/20 text-[#E08850]'
                            }`}>
                                Save 35%
                            </span>
                        </button>
                    </div>
                </div>

                {/* Founding Member Banner */}
                {isFoundingActive && foundingOffer && (
                    <div className="max-w-3xl mx-auto mb-10">
                        <div className="relative overflow-hidden rounded-2xl p-4 sm:p-5 bg-gradient-to-r from-[#1D6B6B] to-[#2A8585] text-white">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                            <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                                    <Gift className="w-6 h-6" />
                                </div>
                                <div className="flex-1">
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                        <span className="font-semibold text-lg">Founding Member Offer</span>
                                        {foundingOffer.days_remaining !== undefined && (
                                            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 text-xs">
                                                <Clock className="w-3 h-3" />
                                                {foundingOffer.days_remaining} days left
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-white/90 text-sm">
                                        {foundingOffer.discount_percent || 25}% off your first year + exclusive Founding Member badge
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Pricing Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-16">
                    {displayTiers.map((tier) => {
                        const Icon = tier.icon || Heart;
                        const isHighlighted = tier.highlighted || tier.id === 'support_plus';
                        const price = getPrice(tier);
                        const originalPrice = getOriginalPrice(tier);
                        const monthlyEquiv = getMonthlyEquivalent(tier);
                        const savings = getSavingsPercent(tier);
                        const isLoading = loadingPackage?.startsWith(tier.id);

                        return (
                            <div 
                                key={tier.id}
                                className={`relative rounded-2xl p-6 flex flex-col transition-all duration-300 ${
                                    isHighlighted 
                                        ? 'bg-gradient-to-b from-[#1D6B6B] to-[#145555] text-white shadow-xl scale-[1.02] lg:scale-105 z-10' 
                                        : 'bg-white shadow-lg hover:shadow-xl border border-[#E8E1D6]'
                                }`}
                                data-testid={`tier-card-${tier.id}`}
                            >
                                {/* Popular Badge */}
                                {isHighlighted && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                        <span className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-[#E08850] text-white text-xs font-semibold shadow-lg">
                                            <Star className="w-3 h-3 fill-current" />
                                            Most Popular
                                        </span>
                                    </div>
                                )}

                                {/* Founding Badge */}
                                {isFoundingActive && tier.monthlyPrice > 0 && originalPrice && (
                                    <div className="absolute -top-3 right-4">
                                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                                            isHighlighted 
                                                ? 'bg-[#E4D3A3] text-[#1D3D3D]' 
                                                : 'bg-[#1D6B6B]/10 text-[#1D6B6B]'
                                        }`}>
                                            <Gift className="w-3 h-3" />
                                            Founding
                                        </span>
                                    </div>
                                )}

                                {/* Header */}
                                <div className="mb-6 pt-2">
                                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                                        isHighlighted 
                                            ? 'bg-white/20' 
                                            : tier.id === 'partner' 
                                                ? 'bg-[#E08850]/10' 
                                                : 'bg-[#1D6B6B]/10'
                                    }`}>
                                        <Icon className={`w-6 h-6 ${
                                            isHighlighted 
                                                ? 'text-white' 
                                                : tier.id === 'partner' 
                                                    ? 'text-[#E08850]' 
                                                    : 'text-[#1D6B6B]'
                                        }`} />
                                    </div>
                                    <h3 className={`text-xl font-bold mb-1 ${isHighlighted ? 'text-white' : 'text-[#1D3D3D]'}`}>
                                        {tier.name}
                                    </h3>
                                    <p className={`text-sm ${isHighlighted ? 'text-white/80' : 'text-[#7D8E95]'}`}>
                                        {tier.description}
                                    </p>
                                </div>

                                {/* Pricing */}
                                <div className="mb-6">
                                    <div className="flex items-baseline gap-2">
                                        <span className={`text-4xl font-bold ${isHighlighted ? 'text-white' : 'text-[#1D3D3D]'}`}>
                                            {price === 0 ? `${getCurrencyInfo(currency).symbol}0` : formatPrice(price, currency)}
                                        </span>
                                        {price > 0 && (
                                            <span className={`text-sm ${isHighlighted ? 'text-white/70' : 'text-[#7D8E95]'}`}>
                                                /{billingCycle === 'monthly' ? 'mo' : 'yr'}
                                            </span>
                                        )}
                                    </div>
                                    {originalPrice && (
                                        <p className={`text-sm line-through ${isHighlighted ? 'text-white/50' : 'text-[#B0A8A0]'}`}>
                                            {formatPrice(originalPrice, currency)} /{billingCycle === 'monthly' ? 'mo' : 'yr'}
                                        </p>
                                    )}
                                    {monthlyEquiv && (
                                        <p className={`text-sm mt-1 ${isHighlighted ? 'text-white/80' : 'text-[#5A7A7A]'}`}>
                                            {formatPrice(parseFloat(monthlyEquiv), currency)}/mo billed yearly
                                            {savings && (
                                                <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                                                    isHighlighted 
                                                        ? 'bg-white/20 text-white' 
                                                        : 'bg-[#E08850]/10 text-[#E08850]'
                                                }`}>
                                                    Save {savings}%
                                                </span>
                                            )}
                                        </p>
                                    )}
                                </div>

                                {/* Features */}
                                <ul className="space-y-3 mb-6 flex-1">
                                    {(tier.highlights || []).map((feature, idx) => (
                                        <li key={idx} className="flex items-start gap-3">
                                            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                                isHighlighted 
                                                    ? 'bg-white/20' 
                                                    : 'bg-[#1D6B6B]/10'
                                            }`}>
                                                <Check className={`w-3 h-3 ${isHighlighted ? 'text-white' : 'text-[#1D6B6B]'}`} />
                                            </div>
                                            <span className={`text-sm ${isHighlighted ? 'text-white/90' : 'text-[#5A7A7A]'}`}>
                                                {feature}
                                            </span>
                                        </li>
                                    ))}
                                </ul>

                                {/* CTA Button */}
                                <button
                                    onClick={() => handleCheckout(tier.id)}
                                    disabled={loadingPackage !== null}
                                    className={`w-full py-3.5 px-6 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
                                        isHighlighted
                                            ? 'bg-white text-[#1D6B6B] hover:bg-white/70 shadow-lg'
                                            : tier.id === 'partner'
                                                ? 'bg-[#E08850] text-white hover:bg-[#D07840]'
                                                : tier.id === 'free'
                                                    ? 'bg-[#E8E1D6] text-[#5A7A7A] hover:bg-[#DDD6CB]'
                                                    : 'bg-[#1D6B6B] text-white hover:bg-[#145555]'
                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                                    data-testid={`cta-${tier.id}`}
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Processing...
                                        </>
                                    ) : (
                                        tier.cta
                                    )}
                                </button>
                            </div>
                        );
                    })}
                </div>

                {/* Feature Comparison Table */}
                <div className="mb-16">
                    <div className="text-center mb-8">
                        <h2 className="text-2xl sm:text-3xl font-bold text-[#1D3D3D] mb-3">
                            Compare all features
                        </h2>
                        <p className="text-[#7D8E95]">
                            See exactly what's included in each plan
                        </p>
                    </div>

                    <div className="bg-white rounded-2xl shadow-lg border border-[#E8E1D6] overflow-hidden">
                        {/* Table Header - Desktop */}
                        <div className="hidden lg:grid grid-cols-5 bg-white/70 border-b border-[#E8E1D6]">
                            <div className="p-5 font-semibold text-[#1D3D3D]">Features</div>
                            <div className="p-5 text-center">
                                <div className="font-semibold text-[#1D3D3D]">Free</div>
                                <div className="text-sm text-[#7D8E95]">{getCurrencyInfo(currency).symbol}0</div>
                            </div>
                            <div className="p-5 text-center">
                                <div className="font-semibold text-[#1D3D3D]">Essentials</div>
                                <div className="text-sm text-[#7D8E95]">{billingCycle === 'monthly' ? formatPrice(14.99, currency) + '/mo' : formatPrice(119, currency, false) + '/yr'}</div>
                            </div>
                            <div className="p-5 text-center bg-[#1D6B6B]/5">
                                <div className="font-semibold text-[#1D6B6B]">Support+</div>
                                <div className="text-sm text-[#5A7A7A]">{billingCycle === 'monthly' ? formatPrice(29.99, currency) + '/mo' : formatPrice(229, currency, false) + '/yr'}</div>
                            </div>
                            <div className="p-5 text-center">
                                <div className="font-semibold text-[#E08850]">Partner</div>
                                <div className="text-sm text-[#7D8E95]">{billingCycle === 'monthly' ? formatPrice(49.99, currency) + '/mo' : formatPrice(379, currency, false) + '/yr'}</div>
                            </div>
                        </div>

                        {/* Feature Categories */}
                        {featureCategories.slice(0, showAllFeatures ? undefined : 2).map((category, catIdx) => (
                            <div key={category.name}>
                                {/* Category Header */}
                                <div className="bg-[#FDF8F4] px-5 py-3 border-b border-[#E8E1D6]">
                                    <span className="font-semibold text-[#1D6B6B] text-sm">{category.name}</span>
                                </div>
                                
                                {/* Features */}
                                {category.features.map((feature, idx) => (
                                    <div 
                                        key={feature.name}
                                        className={`grid grid-cols-1 lg:grid-cols-5 border-b border-[#E8E1D6] last:border-b-0 ${
                                            idx % 2 === 0 ? 'bg-white' : 'bg-[#FEFCFA]'
                                        }`}
                                    >
                                        {/* Feature Name */}
                                        <div className="p-4 lg:p-5 text-[#5A7A7A] text-sm lg:border-r border-[#E8E1D6]">
                                            {feature.name}
                                        </div>
                                        
                                        {/* Mobile: Show all values in a row */}
                                        <div className="grid grid-cols-4 lg:contents">
                                            {/* Free */}
                                            <div className="p-3 lg:p-5 flex items-center justify-center border-r lg:border-r-0 border-[#E8E1D6]">
                                                {feature.free ? (
                                                    <div className="w-6 h-6 rounded-full bg-[#1D6B6B]/10 flex items-center justify-center">
                                                        <Check className="w-4 h-4 text-[#1D6B6B]" />
                                                    </div>
                                                ) : (
                                                    <span className="text-[#D0C8C0]">—</span>
                                                )}
                                            </div>
                                            {/* Essentials */}
                                            <div className="p-3 lg:p-5 flex items-center justify-center border-r lg:border-r-0 border-[#E8E1D6]">
                                                {feature.essentials ? (
                                                    <div className="w-6 h-6 rounded-full bg-[#1D6B6B]/10 flex items-center justify-center">
                                                        <Check className="w-4 h-4 text-[#1D6B6B]" />
                                                    </div>
                                                ) : (
                                                    <span className="text-[#D0C8C0]">—</span>
                                                )}
                                            </div>
                                            {/* Support+ */}
                                            <div className="p-3 lg:p-5 flex items-center justify-center bg-[#1D6B6B]/5 border-r lg:border-r-0 border-[#E8E1D6]">
                                                {feature.support ? (
                                                    <div className="w-6 h-6 rounded-full bg-[#1D6B6B]/20 flex items-center justify-center">
                                                        <Check className="w-4 h-4 text-[#1D6B6B]" />
                                                    </div>
                                                ) : (
                                                    <span className="text-[#D0C8C0]">—</span>
                                                )}
                                            </div>
                                            {/* Partner */}
                                            <div className="p-3 lg:p-5 flex items-center justify-center">
                                                {feature.partner ? (
                                                    <div className="w-6 h-6 rounded-full bg-[#E08850]/10 flex items-center justify-center">
                                                        <Check className="w-4 h-4 text-[#E08850]" />
                                                    </div>
                                                ) : (
                                                    <span className="text-[#D0C8C0]">—</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}

                        {/* Show More/Less Toggle */}
                        <button
                            onClick={() => setShowAllFeatures(!showAllFeatures)}
                            className="w-full p-4 text-[#1D6B6B] font-medium text-sm hover:bg-white/70 transition-colors flex items-center justify-center gap-2"
                            data-testid="toggle-features"
                        >
                            {showAllFeatures ? (
                                <>
                                    Show less <ChevronUp className="w-4 h-4" />
                                </>
                            ) : (
                                <>
                                    Show all features <ChevronDown className="w-4 h-4" />
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Trust Signals */}
                <div className="text-center mb-16">
                    <div className="inline-flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-sm text-[#7D8E95]">
                        <div className="flex items-center gap-2">
                            <Shield className="w-5 h-5 text-[#1D6B6B]" />
                            <span>Secure payment</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-[#1D6B6B]" />
                            <span>Cancel anytime</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Heart className="w-5 h-5 text-[#1D6B6B]" />
                            <span>Made for ADHD minds</span>
                        </div>
                    </div>
                </div>

                {/* FAQ Preview */}
                <div className="max-w-2xl mx-auto text-center">
                    <p className="text-[#7D8E95] mb-4">Have questions?</p>
                    <button
                        onClick={() => navigate('/help')}
                        className="text-[#1D6B6B] font-medium hover:underline"
                        data-testid="faq-link"
                    >
                        Visit our FAQ page
                    </button>
                </div>
            </main>
        </div>
    );
};

export default Paywall;
